import { ReactNode, forwardRef } from 'react';

import { Badge as RadixBadge } from '@radix-ui/themes';
import type { ComponentPropsWithoutRef } from 'react';
import { useThemeContext } from '@radix-ui/themes';
import clsx from 'clsx';
import { resolveColor as resolveColorUtil } from '@/utils/colorUtils';

type RadixBadgeProps = ComponentPropsWithoutRef<typeof RadixBadge>;

export type BadgeVariant =
  | 'solid'
  | 'soft'
  | 'surface'
  | 'outlined'
  | 'dashed'
  | 'ghost';
export type BadgeColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'feature'
  | 'destructive'
  | 'orange'
  | 'blue'
  | 'red'
  | 'green'
  | 'gray'
  | 'teal';
export type BadgeSize = 'xsmall' | 'small' | 'medium' | 'large';

interface BaseBadgeProps {
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  color?: BadgeColor;
  variant?: BadgeVariant;
  size?: BadgeSize;
  children?: ReactNode;
  className?: string;
  radius?: 'none' | 'xsmall' | 'small' | 'medium' | 'large' | 'full';
  pill?: boolean;
}

export type BadgeProps = Omit<
  ComponentPropsWithoutRef<typeof RadixBadge>,
  'color' | 'size' | 'variant'
> &
  BaseBadgeProps;

const sizeMapping = {
  xsmall: '1',
  small: '1',
  medium: '2',
  large: '3',
} as const;

const variantMapping = {
  solid: 'solid',
  soft: 'soft',
  surface: 'surface',
  outlined: 'outline',
  dashed: 'outline',
  ghost: 'soft',
} as const;

const radiusMapping = {
  none: 'none',
  xsmall: 'small',
  small: 'small',
  medium: 'medium',
  large: 'large',
  full: 'full',
} as const;

function resolveColor(
  color: BadgeColor,
  accentColor?: string
): {
  color: RadixBadgeProps['color'];
  highContrast?: boolean;
} {
  switch (color) {
    case 'primary':
      return {
        color: resolveColorUtil<RadixBadgeProps['color']>(color, accentColor || undefined),
      };
    case 'secondary':
      return { color: 'gray', highContrast: true };
    default:
      return { color: resolveColorUtil<RadixBadgeProps['color']>(color, accentColor || undefined) };
  }
}

const Badge = forwardRef<HTMLDivElement, BadgeProps>(
  (
    {
      variant = 'surface',
      color = 'default',
      size = 'medium',
      radius = 'medium',
      pill = false,
      className,
      startIcon,
      endIcon,
      children,
      ...props
    },
    ref
  ) => {
    const theme = useThemeContext();
    const accentColor = theme?.accentColor || 'indigo';
    const { color: resolvedColor, highContrast = false } = resolveColor(
      color,
      accentColor
    );
    const mappedSize = sizeMapping[size];
    const mappedVariant = variantMapping[variant];
    const mappedRadius = pill ? 'full' : radiusMapping[radius];

    const renderContent = () => (
      <>
        {startIcon && (
          <span className="badge__icon badge__icon--start">{startIcon}</span>
        )}
        <span className="badge__label">{children}</span>
        {endIcon && (
          <span className="badge__icon badge__icon--end">{endIcon}</span>
        )}
      </>
    );

    return (
      <RadixBadge
        ref={ref}
        color={resolvedColor}
        variant={mappedVariant}
        radius={mappedRadius}
        size={mappedSize}
        highContrast={highContrast}
        className={clsx(
          'badge',
          `badge-size-${size}`,
          `badge-color-${color}`,
          `badge-variant-${variant}`,
          className
        )}
        {...props}
      >
        {renderContent()}
      </RadixBadge>
    );
  }
);

Badge.displayName = 'Badge';

export { Badge };
